import { extractDigits } from '@/utils/string-formatting.js';

/**
 * What a text field keeps of what is typed or pasted into it, worked out from its validation rules
 * (like the keyboard hints in input-hints.js), so people can enter a value the way they write it.
 *
 *   phone   "(514) 111-5555", "+1 514 111 5555"  ->  "5141115555"
 *   SSN/SIN "078-05-1120", "123 456 789"         ->  "078051120", "123456789"
 *   number  "$4,500", "4 500", "4500.00"         ->  "4500"
 *   name    "O’Neil" (iOS smart punctuation)     ->  "O'Neil"
 *
 * It runs on every keystroke, so each step of the typing has to be safe on its own. The field
 * still holds (and submits) digits only, exactly as before: lms and newform's own rules accept
 * plain digits, and lms 422s on "10,000". Each format also says how many characters the input may
 * take, since a maxlength of 10 cut "(514) 111-5555" to "(514) 111-" before any of this ran; the
 * digit limit is enforced here instead.
 *
 * A decimal can't be dropped as it is typed: the "." would vanish and the cents that follow would
 * be read as more digits ("4500.50" -> 450050). So the box keeps it, and the field holds (and sends)
 * what lms takes, a whole number: that is a format's `toValue`. A number drops its decimal part
 * ("321.50" or a French "321,50" -> 321); a rate, known as 6.99 or 6,99, is rounded (-> 7). The box
 * shows the held value once the person leaves it (LcField.vue).
 */

const PHONE_RULES = ['phone', 'canadianPhone', 'usPhone'];
const NINE_DIGIT_ID_RULES = ['ssn', 'sin'];
const NUMBER_RULES = ['numeric'];
const RATE_RULES = ['rate'];
const NAME_RULES = ['name'];

const PHONE_DIGITS = 10;
const ID_DIGITS = 9;

// room for "+1 (514) 111-5555" and "514.111.5555 " with a stray space
const PHONE_MAXLENGTH = 20;
// "078-05-1120", "123 456 789"
const ID_MAXLENGTH = 11;
// "$", separators and ".00" around the digits a number field allows
const NUMBER_FORMATTING_ROOM = 8;
// a rate up to 99.99; room for "12,99 %" pasted with a stray space
const RATE_WHOLE_DIGITS = 2;
const RATE_DECIMALS = 2;
const RATE_MAXLENGTH = 8;

// regular, no-break and narrow no-break spaces (fr-CA writes 4 500 with the latter two)
const NUMBER_NOISE_REGEX = /[\s  $]/g;
// a comma followed by exactly three digits is a thousands separator: 4,500 and 1,250,000
const THOUSANDS_COMMA_REGEX = /,(?=\d{3}(?!\d))/g;
// ".00" only: ",00" is also what "1,000" looks like halfway through being typed
const ZERO_CENTS_REGEX = /\.00$/;
// a decimal part, "." or "," and all after it, once thousands commas are gone: "4500.75", "4500,75"
const DECIMAL_PART_REGEX = /[.,].*$/;
// spaces (fr-CA writes 6,99 % with a no-break one) and the % the field already shows after the box
const RATE_NOISE_REGEX = /[\s  %]/g;
// digits, at most one "." or "," (a French decimal comma), digits: "6", "6.", "6.99", ",5"
const RATE_PARTS_REGEX = /^(\d*)([.,]?)(\d*)$/;
// ’ (what iOS and macOS type for ') and its look-alikes
const CURLY_APOSTROPHES_REGEX = /[‘’ʼ]/g;

function ruleNames(rules) {
    return String(rules ?? '')
        .split('|')
        .map(rule => rule.split(':')[0].trim())
        .filter(Boolean);
}

/**
 * Digits of a phone number, at most 10. A leading 1 (the +1 country code) is dropped once there
 * are 11 digits; no North American number starts with 1, so it cannot be part of the number.
 */
export function phoneDigits(value) {
    let digits = extractDigits(value ?? '');
    if (digits.length > PHONE_DIGITS && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    return digits.slice(0, PHONE_DIGITS);
}

function nineDigitId(value) {
    return extractDigits(value ?? '').slice(0, ID_DIGITS);
}

/**
 * A whole number without "$", spaces, thousands commas or zero cents. Anything else is left as
 * typed for the numbers-only rule to report. `maxDigits` caps a plain number, as maxlength did.
 */
export function plainNumber(value, maxDigits = null) {
    const cleaned = String(value ?? '')
        .replace(NUMBER_NOISE_REGEX, '')
        .replace(THOUSANDS_COMMA_REGEX, '')
        .replace(ZERO_CENTS_REGEX, '');
    if (maxDigits && /^\d+$/.test(cleaned)) {
        return cleaned.slice(0, maxDigits);
    }
    return cleaned;
}

/**
 * The whole number a number field holds: what is typed without its decimal part, dropped rather
 * than rounded ("$4,500.75" -> 4500, "321.123" -> 321). Anything that still isn't digits (letters,
 * a lone ".5") is left as typed for the numbers-only rule to report.
 */
export function wholeNumber(value, maxDigits = null) {
    const kept = plainNumber(value, maxDigits);
    const whole = kept.replace(DECIMAL_PART_REGEX, '');
    if (whole === kept || !/^\d+$/.test(whole)) {
        return kept;
    }
    return maxDigits ? whole.slice(0, maxDigits) : whole;
}

/**
 * A rate as typed, without spaces or "%", at most 99.99. The person's own "." or "," stays, so the
 * box shows what they typed. Anything else (letters, a second separator) is left for the rule to
 * report.
 */
export function typedRate(value) {
    const cleaned = String(value ?? '').replace(RATE_NOISE_REGEX, '');
    const parts = cleaned.match(RATE_PARTS_REGEX);
    if (!parts) {
        return cleaned;
    }
    const [, whole, separator, decimals] = parts;
    return whole.slice(0, RATE_WHOLE_DIGITS) + separator + decimals.slice(0, RATE_DECIMALS);
}

/**
 * The whole number a rate is kept and sent as, rounded to the nearest: 6.99 -> 7, 6.5 -> 7,
 * 6.49 -> 6. What cannot be read as a rate comes back as typed, for the rule to report.
 */
export function wholeRate(value) {
    const typed = typedRate(value);
    const [, whole = '', , decimals = ''] = typed.match(RATE_PARTS_REGEX) ?? [];
    if (whole === '' && decimals === '') {
        return typed;
    }
    return String(Math.round(Number(`${whole || '0'}.${decimals || '0'}`)));
}

/**
 * A name with straight apostrophes. lms deletes a straight ' when it saves a name (O'Neil -> Oneil)
 * but keeps a curly one, which would then go to lead buyers as is.
 */
export function straightApostrophes(value) {
    return String(value ?? '').replace(CURLY_APOSTROPHES_REGEX, "'");
}

/**
 * The format for a text field: { normalize(value) => kept value, maxlength } or null when the field
 * takes what is typed as is. `maxlength` is the schema's own limit; a number field reads it as a
 * number of digits. A number or a rate also has `toValue(kept) => value the field holds`.
 */
export function inputFormatFor(rules, maxlength = undefined) {
    const names = ruleNames(rules);

    if (names.some(rule => PHONE_RULES.includes(rule))) {
        return { normalize: phoneDigits, maxlength: PHONE_MAXLENGTH };
    }
    if (names.some(rule => NINE_DIGIT_ID_RULES.includes(rule))) {
        return { normalize: nineDigitId, maxlength: ID_MAXLENGTH };
    }
    if (names.some(rule => NUMBER_RULES.includes(rule))) {
        const maxDigits = Number(maxlength) || null;
        return {
            normalize: value => plainNumber(value, maxDigits),
            toValue: value => wholeNumber(value, maxDigits),
            maxlength: maxDigits ? maxDigits + NUMBER_FORMATTING_ROOM : undefined,
        };
    }
    if (names.some(rule => RATE_RULES.includes(rule))) {
        return { normalize: typedRate, toValue: wholeRate, maxlength: RATE_MAXLENGTH };
    }
    if (names.some(rule => NAME_RULES.includes(rule))) {
        return { normalize: straightApostrophes, maxlength };
    }
    return null;
}
